import { useLocation } from "wouter";
import { useState, useEffect } from "react";
import { Badge, Card, CardContent, CardHeader, CardTitle } from "@/ui";
import { MapPin, Clock, Users, Heart, Award, ArrowRight } from "lucide-react";
const careersImage = "/assets/website/images/Healthcare_careers_and_professionals_f3c73416.png";
import { CareerService, Career } from "@/services/careerService";

export default function Careers() {
  const [, setLocation] = useLocation();
  const [careers, setCareers] = useState<Career[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadCareers = async () => {
      try {
        const activeCareers = await CareerService.getActiveCareers();
        setCareers(activeCareers);
      } catch (error) {
        console.error('Error loading careers:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCareers();
  }, []);

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  if (loading) {
    return (
      <div
        className="min-h-screen font-sans"
        style={{
          backgroundImage: "url('/assets/shared/simple-bg.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundAttachment: "fixed",
          fontFamily: "Montserrat, system-ui, sans-serif",
        }}
      >
        <main className="py-20">
          <div className="container mx-auto px-4 text-center">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-1/4 mx-auto mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
            </div>
          </div>
        </main>
      </div>
    );
  }
  
  return (
    <div
      className="min-h-screen font-sans"
      style={{
        backgroundImage: "url('/assets/shared/simple-bg.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
        fontFamily: "Montserrat, system-ui, sans-serif",
      }}
    >
      <main className="py-20">
        {/* Hero Image Section */}
        <div className="relative h-80 mb-20 overflow-hidden">
          <img 
            src={careersImage} 
            alt="Healthcare Careers and Professionals"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/80 to-background/60" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <Badge variant="secondary" className="mb-4" data-testid="badge-careers">
                Careers
              </Badge>
              <h1 className="text-3xl md:text-4xl font-normal text-primary mb-4" data-testid="text-careers-title" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                Join Our Mission to Transform Healthcare
              </h1>
              <p className="text-xl text-foreground max-w-3xl mx-auto" data-testid="text-careers-description" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                Help us build the future of healthcare through intelligent AI solutions. 
                Join a team of passionate professionals making a real impact.
              </p>
            </div>
          </div>
        </div>
        
        <div className="container mx-auto px-4">

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <Card className="text-center border-2 hover:shadow-xl transition-all duration-300 hover:-translate-y-1" data-testid="card-culture-innovation">
              <CardHeader>
                <div className="flex items-center justify-center w-16 h-16 bg-primary/10 rounded-lg mx-auto mb-4">
                  <Award className="h-8 w-8 text-primary" />
                </div>
                <CardTitle>Innovation First</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Work on cutting-edge AI technology that directly impacts patient care and clinical workflows
                </p>
              </CardContent>
            </Card>

            <Card className="text-center border-2 hover:shadow-xl transition-all duration-300 hover:-translate-y-1" data-testid="card-culture-team">
              <CardHeader>
                <div className="flex items-center justify-center w-16 h-16 bg-primary/10 rounded-lg mx-auto mb-4">
                  <Users className="h-8 w-8 text-primary" />
                </div>
                <CardTitle>Collaborative Team</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Join healthcare professionals, AI researchers, and engineers working together toward common goals
                </p>
              </CardContent>
            </Card>

            <Card className="text-center border-2 hover:shadow-xl transition-all duration-300 hover:-translate-y-1" data-testid="card-culture-impact">
              <CardHeader>
                <div className="flex items-center justify-center w-16 h-16 bg-primary/10 rounded-lg mx-auto mb-4">
                  <Heart className="h-8 w-8 text-primary" />
                </div>
                <CardTitle>Meaningful Work</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Every line of code and every feature directly improves healthcare delivery and patient outcomes
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="mb-16">
            <h2 className="text-2xl font-normal text-center mb-12" data-testid="text-openings-title" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
              Current Openings
            </h2>
            <div className="max-w-5xl mx-auto space-y-8">
              {careers.map((career, index) => (
                <article 
                  key={career.id}
                  className="group bg-white/80 backdrop-blur-sm rounded-2xl p-8 md:p-10 border-l-4 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
                  style={{ borderLeftColor: "#1356a6" }}
                  data-testid={`card-job-${index + 1}`}
                >
                  {/* Header Section */}
                  <div className="mb-6">
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                      <Badge variant="secondary" className="text-sm px-3 py-1">{career.department}</Badge>
                      <div className="flex items-center gap-4 text-sm" style={{ color: "#64748b" }}>
                        <div className="flex items-center gap-1">
                          <MapPin className="h-4 w-4" />
                          <span>{career.location}</span>
                        </div>
                        <span>•</span>
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          <span>{career.employment_type}</span>
                        </div>
                      </div>
                    </div>
                    <h3 
                      className="text-3xl md:text-4xl font-normal mb-3 group-hover:text-primary transition-colors"
                      style={{ fontFamily: "Montserrat, system-ui, sans-serif", color: "#1356a6" }}
                    >
                      {career.title}
                    </h3>
                  </div>

                  {/* Content Section */}
                  <div className="space-y-6">
                    <div>
                      <h4 className="font-semibold mb-2" style={{ color: "#1356a6" }}>About Aiscendus</h4>
                      <p className="text-base leading-relaxed" style={{ color: "#475569" }}>
                        Aiscendus is pioneering the next generation of Agentic AI platforms for healthcare, enabling intelligent, 
                        autonomous agents that collaborate across clinical, operational, and administrative workflows. Our platform 
                        integrates seamlessly with leading EHRs (Epic, Cerner, AthenaHealth, and others), combining AI automation, 
                        ambient intelligence, and secure cloud-native orchestration to transform care delivery and drive value-based outcomes.
                      </p>
                    </div>
                    
                    {career.description && (
                      <div>
                        <h4 className="font-semibold mb-2" style={{ color: "#1356a6" }}>Role Overview</h4>
                        <p className="text-base leading-relaxed" style={{ color: "#475569" }}>{career.description}</p>
                      </div>
                    )}
                    
                    {career.responsibilities && (
                      <div>
                        <h4 className="font-semibold mb-3" style={{ color: "#1356a6" }}>Key Responsibilities</h4>
                        <ul className="text-base space-y-2 list-none" style={{ color: "#475569" }}>
                          {career.responsibilities.split('. ').filter(item => item.trim()).map((item, index) => (
                            <li key={index} className="flex items-start gap-3">
                              <span className="text-primary mt-1.5">•</span>
                              <span>{item.trim()}{!item.endsWith('.') && '.'}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {career.qualifications && (
                      <div>
                        <h4 className="font-semibold mb-3" style={{ color: "#1356a6" }}>Preferred Qualifications</h4>
                        <ul className="text-base space-y-2 list-none" style={{ color: "#475569" }}>
                          {career.qualifications.split('. ').filter(item => item.trim()).map((item, index) => (
                            <li key={index} className="flex items-start gap-3">
                              <span className="text-primary mt-1.5">•</span>
                              <span>{item.trim()}{!item.endsWith('.') && '.'}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {career.benefits && (
                      <div>
                        <h4 className="font-semibold mb-2" style={{ color: "#1356a6" }}>Benefits</h4>
                        <div className="text-base leading-relaxed whitespace-pre-line" style={{ color: "#475569" }}>{career.benefits}</div>
                      </div>
                    )}

                    {career.salary_range && (
                      <div>
                        <h4 className="font-semibold mb-2" style={{ color: "#1356a6" }}>Salary Range</h4>
                        <p className="text-base" style={{ color: "#475569" }}>{career.salary_range}</p>
                      </div>
                    )}

                    {/* Apply Button */}
                    <div 
                      className="pt-6 mt-6 border-t flex items-center gap-2 font-semibold cursor-pointer group-hover:gap-3 transition-all"
                      style={{ color: "#1356a6" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setLocation(`/careers/apply/${generateSlug(career.title)}`);
                      }}
                    >
                      <span>Apply for this Position</span>
                      <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-xl p-10 shadow-lg">
            <h2 className="text-2xl font-normal text-center mb-8" data-testid="text-benefits-title" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
              Benefits & Perks
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 text-center">
              <div>
                <h3 className="font-semibold text-foreground mb-2">Healthcare Coverage</h3>
                <p className="text-sm text-muted-foreground">Comprehensive medical, dental, and vision insurance</p>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-2">Equity Participation</h3>
                <p className="text-sm text-muted-foreground">Meaningful equity stake in company success</p>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-2">Flexible Work</h3>
                <p className="text-sm text-muted-foreground">Remote-first culture with flexible schedules</p>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-2">Learning Budget</h3>
                <p className="text-sm text-muted-foreground">Annual budget for conferences and professional development</p>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-2">Unlimited PTO</h3>
                <p className="text-sm text-muted-foreground">Take time when you need it to recharge</p>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-2">Team Retreats</h3>
                <p className="text-sm text-muted-foreground">Quarterly in-person team building events</p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}